import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SelectionSlot } from '@entities/selection';
import { Button, Modal } from '@shared/ui';

import styles from './Dialogs.module.css';

interface DeclineDialogProps {
  slot: SelectionSlot | null;
  /** Идёт ли торг по этому автомобилю: тогда подтверждение в два шага. */
  bargaining?: boolean | undefined;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Подтверждение «Отказаться от этого автомобиля» (FE-400, MVP_PRD §34.5).
 *
 * Появляется **только после заморозки состава** и отличается от «Не подходит»
 * ровно последствием, о котором окно и обязано сказать прямо: **замены не
 * будет**. Слот останется пустым, четвёрка станет тройкой, а когда пустыми
 * станут все четыре — заказ уйдёт к итогам (§34.6).
 *
 * **Причины здесь не спрашивают.** У «Не подходит» она нужна разбору
 * исчерпания: там пользователь отсеивает варианты один за другим, и почему —
 * существенно. Здесь он уже прочитал ответы автохауса и принял решение;
 * анкета после проделанной работы выглядит как попытка его отговорить.
 *
 * **Без оптимистичного UI и в два шага при идущем торге** (CLAUDE.md §8):
 * письма уже ушли, автохаус потратил время на ответы, и случайное нажатие не
 * должно этого стирать.
 */
export function DeclineDialog({
  slot,
  bargaining = false,
  busy,
  error,
  onConfirm,
  onClose,
}: DeclineDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const [acknowledged, setAcknowledged] = useState(false);

  const needsSecondStep = bargaining && !acknowledged;

  const close = () => {
    setAcknowledged(false);
    onClose();
  };

  return (
    <Modal
      open={slot !== null}
      onClose={close}
      labelledBy={titleId}
      dismissible={!busy}
      className={styles.dialog}
    >
      <h2 id={titleId} className={styles.title}>
        {t('selection.decline.title')}
      </h2>

      {slot === null ? null : (
        <p className={styles.subject}>
          {slot.vehicle.make} {slot.vehicle.model}
          {slot.vehicle.variant === null ? null : ` · ${slot.vehicle.variant}`}
        </p>
      )}

      {/* Главное предложение окна: замены не будет. Оно стоит первым и не
          прячется в подпись под кнопкой (§34.5). */}
      <p className={styles.warning}>{t('selection.decline.noReplacement')}</p>
      <p className={styles.text}>{t('selection.decline.text')}</p>

      {bargaining ? <p className={styles.warning}>{t('selection.decline.bargaining')}</p> : null}

      {error === null ? null : (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <div className={styles.actions}>
        <Button variant="secondary" disabled={busy} onClick={close}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="danger"
          disabled={busy}
          onClick={() => {
            if (needsSecondStep) {
              setAcknowledged(true);
              return;
            }
            onConfirm();
          }}
        >
          {busy
            ? t('selection.decline.pending')
            : needsSecondStep
              ? t('selection.decline.continue')
              : t('selection.decline.confirm')}
        </Button>
      </div>
    </Modal>
  );
}
